import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NavigationService } from 'src/app/services/navigation.service';
import { Subscription } from 'rxjs';
import { getAbsoluteUrl } from '../../utils/utils';
import { FOOTER_MAP } from './footer.const';
import { BrandService } from 'src/app/services/brand.service';
import {
  BrandKey,
  BrandConfig,
  cloneFooterMap,
} from '../../constants/brand.constants';

interface FooterLink {
  title: string;
  href: string;
}

interface FooterSection {
  title: string;
  href?: string;
  links: FooterLink[];
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent implements OnInit, OnDestroy {
  icons: { [key: string]: SafeHtml };
  hasOfferBar: boolean = false;
  offerSub: Subscription;
  brandSub: Subscription;
  FOOTER_MAP = FOOTER_MAP;
  footerMap = cloneFooterMap();
  brandConfig: BrandConfig;
  newsouthSections: FooterSection[] = [
    {
      title: 'Products',
      links: [
        { title: 'Windows', href: '/replacement-windows/' },
        { title: 'Impact Windows', href: '/impact-windows/' },
        { title: 'Doors', href: '/doors/' },
        { title: 'Eze-Breeze', href: '/eze-breeze/' },
      ],
    },
    {
      title: 'Resources',
      links: [
        { title: 'Colors & Styles', href: '/colors-and-styles/' },
        { title: 'Why Replace?', href: '/why-replace/' },
        { title: 'Window Installation', href: '/window-installation/' },
        { title: 'Videos', href: '/videos/' },
        { title: 'FAQs', href: '/faqs/' },
        { title: 'Blog', href: '/blog/' },
        { title: 'Financing', href: '/easy-low-payments/' },
      ],
    },
    {
      title: 'Company',
      links: [
        { title: 'About Us', href: '/about/' },
        { title: 'Our Showroom', href: '/our-showroom/' },
        { title: 'News', href: '/news/' },
        { title: 'Gallery', href: '/galleries/' },
        { title: 'Customer Reviews', href: '/newsouth-windows-reviews/' },
        { title: 'Specials', href: '/specials/' },
        { title: 'Careers', href: 'https://jobs.lever.co/windownation?department=NewSouth' },
        { title: 'Contact Us', href: '/contact-us/' },
        { title: 'Privacy Policy', href: '/privacy-policy/' },
      ],
    },
    {
      title: 'Locations',
      href: 'https://newsouthwindow.com/locations/',
      links: [
        { title: 'Tampa, FL', href: '/locations/tampa/' },
        { title: 'Orlando, FL', href: '/locations/orlando/' },
        { title: 'Jacksonville, FL', href: '/locations/jacksonville/' },
        { title: 'Sarasota, FL', href: '/locations/sarasota/' },
        { title: 'West Palm Beach, FL', href: '/locations/west-palm-beach/' },
        { title: 'Ft. Lauderdale, FL', href: '/locations/ft-lauderdale/' },
        { title: 'Pensacola, FL', href: '/locations/pensacola/' },
        { title: 'Bonita Springs, FL', href: '/locations/bonita-springs/' },
        { title: 'Charleston, SC', href: '/locations/charleston/' },
      ],
    },
  ];
  armorvueSections: FooterSection[] = [
    {
      title: 'Locations',
      links: [
        { title: 'Bonita Springs FL', href: '/locations/bonita-springs/' },
        { title: 'Jacksonville FL', href: '/locations/jacksonville-fl/' },
        { title: 'Orlando FL', href: '/locations/orlando-fl/' },
        { title: 'Sarasota FL', href: '/locations/sarasota-fl/' },
        { title: 'South Florida', href: '/locations/south-florida/' },
        { title: 'Tampa FL', href: '/locations/tampa-fl/' },
        { title: 'View All Locations', href: '/location-contact/' },
      ],
    },
    {
      title: 'Help',
      links: [
        { title: 'Contact Us', href: '/location-contact/' },
        { title: 'Warranty', href: '/offers/warranty/' },
        { title: 'Financing', href: '/offers/financing/' },
        { title: 'Customer Service', href: '/customer-service/' },
        { title: 'Service Areas', href: '/location-contact/' },
        { title: 'Careers', href: 'https://jobs.lever.co/windownation?department=NewSouth&team=Sales%20-%20ARMORVUE' },
      ],
    },
    {
      title: 'Company',
      links: [
        { title: 'FAQs', href: '/faqs/' },
        { title: 'Blogs', href: '/blogs/' },
        { title: 'Glossary', href: '/glossary/' },
        { title: 'News', href: '/news/' },
        { title: 'Reviews', href: '/reviews/' },
        { title: 'Factory Partners', href: '/factory-partners/' },
        { title: 'Hurricane Preparedness', href: '/hurricane-preparedness/' },
      ],
    },
  ];
  armorvuePolicyLinks: FooterLink[] = [
    { title: 'Privacy Policy', href: '/privacy-policy/' },
    { title: 'Site Map', href: '/sitemap/' },
  ];
  newsouthContactPhone = '866-638-5831';
  newsouthSocialLinks: FooterLink[] = [
    { title: 'YouTube', href: 'https://www.youtube.com/channel/UC2JGtjyoO6olH2zD6iKHDCQ' },
    { title: 'Facebook', href: 'https://www.facebook.com/NewSouthWindow' },
    { title: 'LinkedIn', href: 'https://www.linkedin.com/company/newsouth-window-solutions/' },
    { title: 'X', href: 'https://x.com/NewSouthWindow' },
  ];
  armorvueSocialLinks: FooterLink[] = [
    { title: 'Facebook', href: 'https://www.facebook.com/armorvuefl' },
    { title: 'YouTube', href: 'https://www.youtube.com/channel/UCn1Nrf3r73DE3LlXZSo8HSw/playlists' },
    { title: 'Instagram', href: 'https://www.instagram.com/armorvue/' },
  ];

  constructor(
    private sanitizer: DomSanitizer,
    private navigationService: NavigationService,
    private brandService: BrandService
  ) {
    this.brandConfig = this.brandService.getCurrentBrandConfig();
    this.applyBrandFooterConfig(this.brandConfig);

    this.icons = {
      Facebook: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="img" style="color:var(--icon-color, currentcolor);width:var(--icon-width, var(--icon-size, 32px));height:var(--icon-height, var(--icon-size, 32px))"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>`
      ),
      X: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="img" style="color:var(--icon-color, currentcolor);width:var(--icon-width, var(--icon-size, 32px));height:var(--icon-height, var(--icon-size, 32px))"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18 6 6 18M6 6l12 12"></path></svg>`
      ),
      Linkedin: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="img" style="color:var(--icon-color, currentcolor);width:var(--icon-width, var(--icon-size, 32px));height:var(--icon-height, var(--icon-size, 32px))"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6M6 9H2v12h4zM4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4"></path></svg>`
      ),
      Instagram: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="img" style="color:var(--icon-color, currentcolor);width:var(--icon-width, var(--icon-size, 32px));height:var(--icon-height, var(--icon-size, 32px))"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17.5 6.5h.01M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5m9 9.37a4 4 0 1 1-7.914 1.174A4 4 0 0 1 16 11.37"></path></svg>`
      ),
      Youtube: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" role="img" style="color:var(--icon-color, currentcolor);width:var(--icon-width, var(--icon-size, 32px));height:var(--icon-height, var(--icon-size, 32px))"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2c.312-1.732.466-3.49.46-5.25a29 29 0 0 0-.46-5.33"></path><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m9.75 15.02 5.75-3.27-5.75-3.27z"></path></svg>`
      ),
      Pinterest: '',
      ArmorvueFacebook: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 20" fill="currentColor" width="15" height="15"><path d="M6.5 7V5.25C6.5 4.284 6.784 3.5 8.25 3.5H10V0H7.25C4.317 0 2.5 1.817 2.5 5v2H0v3.5h2.5V20h4V10.5h3.25L10.5 7H6.5Z"/></svg>`
      ),
      ArmorvueYoutube: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/></svg>`
      ),
      ArmorvueInstagram: this.sanitizer.bypassSecurityTrustHtml(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="15" height="15"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>`
      ),
    };
  }
  ngOnInit() {
    this.offerSub = this.navigationService.getHasOfferBar().subscribe((res) => {
      this.hasOfferBar = res;
    });

    this.brandSub = this.brandService.brand$.subscribe((brandConfig) => {
      this.brandConfig = brandConfig;
      this.applyBrandFooterConfig(brandConfig);
    });
  }

  private applyBrandFooterConfig(brandConfig: BrandConfig): void {
    const nextFooterMap = cloneFooterMap();
    if (nextFooterMap.linkLists[3]) {
      nextFooterMap.linkLists[3].header = brandConfig.content.footerCompanySectionTitle;
    }
    nextFooterMap.subfooter.copyrightLanguage =
      brandConfig.content.footerCopyright;
    this.footerMap = nextFooterMap;
  }

  get isNewsouthBrand(): boolean {
    return this.brandConfig?.key === 'newsouth';
  }

  get isArmorvueBrand(): boolean {
    return this.brandConfig?.key === 'armorvue';
  }

  get isWindowNationBrand(): boolean {
    return this.brandConfig?.key === 'windownation';
  }

  get newsouthCopyright(): string {
    return '© 2026 NewSouth Window Solutions. All rights reserved.';
  }

  get newsouthLicenseText(): string {
    return 'LIC# G127478 / SCC131153607';
  }

  get armorvueCopyright(): string {
    return '© 2013 - 2026 ARMORVUE Window & Door. All Rights Reserved.';
  }

  getBrandClassName(): BrandKey {
    return this.brandConfig?.key ?? 'windownation';
  }

  modifyUrl(link: string){
    return getAbsoluteUrl(link, this.brandConfig.siteBaseUrl);
  }

  ngOnDestroy(): void {
    if (this.offerSub) this.offerSub.unsubscribe();
    if (this.brandSub) this.brandSub.unsubscribe();
  }
}
